import { apiFetch } from '../utils/apiFetch';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { API_BASE_URL } from '../utils/apiBaseUrl';

function authHeaders(): HeadersInit | undefined {
  const token = localStorage.getItem('lts_jwt_token')
  return token ? { Authorization: `Bearer ${token}` } : undefined
}

const REMEMBER_DEVICE_KEY = 'lts_remember_device'

export const authService = {
  async getAuthConfig(): Promise<{
    mfaRememberDays: number;
    sessionInactivityTimeoutMinutes: number;
    sessionWarningCountdownSeconds: number;
    webauthnEnabled?: boolean;
  }> {
    const res = await fetch(`${API_BASE_URL}/auth/config`)
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Failed to load auth configuration')
    }
    return {
      mfaRememberDays: Number(data.mfaRememberDays) || 7,
      sessionInactivityTimeoutMinutes: Number(data.sessionInactivityTimeoutMinutes) || 60,
      sessionWarningCountdownSeconds: Number(data.sessionWarningCountdownSeconds) || 60,
      webauthnEnabled: !!data.webauthnEnabled
    }
  },

  getRememberDeviceToken(): string | null {
    return localStorage.getItem(REMEMBER_DEVICE_KEY)
  },

  setRememberDeviceToken(token: string | null): void {
    if (token) localStorage.setItem(REMEMBER_DEVICE_KEY, token)
    else localStorage.removeItem(REMEMBER_DEVICE_KEY)
  },

  async trustedDeviceLogin(ticket: string, rememberDeviceToken: string): Promise<{ token: string; user: any }> {
    const res = await fetch(`${API_BASE_URL}/auth/trusted-device`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, rememberDeviceToken })
    })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'Trusted device login failed')
    return data
  },

  async mfaVerify(ticket: string, otp?: string, recoveryCode?: string, rememberDevice?: boolean): Promise<{ token: string; user: any; rememberDeviceToken?: string }> {
    const res = await fetch(`${API_BASE_URL}/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, otp, recoveryCode, rememberDevice })
    })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'MFA verification failed')
    return data
  },

  async mfaSetupStart(ticket?: string): Promise<{ otpauth: string; secretPreview: string }> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/setup/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() || {}) },
      body: JSON.stringify(ticket ? { ticket } : {})
    })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to start MFA setup')
    return data
  },

  async mfaSetupVerify(code: string, ticket?: string, preserveRecovery?: boolean): Promise<{ recoveryCodes?: string[]; token?: string; user?: any }> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/setup/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() || {}) },
      body: JSON.stringify(ticket ? { code, ticket, preserveRecovery } : { code, preserveRecovery })
    })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to verify MFA setup')
    return data
  },

  async mfaStatus(): Promise<{ enabled: boolean; remainingRecoveryCodes: number; passkeyCount?: number; methods?: string[] }> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/status`, { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to get MFA status')
    return data
  },

  async mfaPasskeyRegister(ticket?: string): Promise<{ token?: string; user?: any }> {
    const startRes = await apiFetch(`${API_BASE_URL}/auth/mfa/passkey/register/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() || {}) },
      body: JSON.stringify(ticket ? { ticket } : {})
    });
    const startData = await startRes.json();
    if (!startRes.ok || !startData.success) throw new Error(startData.message || 'Failed to start passkey setup');
    const credential = await startRegistration(startData.options);

    const finishRes = await apiFetch(`${API_BASE_URL}/auth/mfa/passkey/register/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(authHeaders() || {}) },
      body: JSON.stringify({ credential, ...(ticket ? { ticket } : {}) })
    });
    const finishData = await finishRes.json();
    if (!finishRes.ok || !finishData.success) throw new Error(finishData.message || 'Failed to finish passkey setup');
    return finishData;
  },

  async mfaPasskeyAuthenticate(ticket: string, rememberDevice?: boolean): Promise<{ token: string; user: any; rememberDeviceToken?: string }> {
    const startRes = await fetch(`${API_BASE_URL}/auth/mfa/passkey/auth/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket })
    });
    const startData = await startRes.json();
    if (!startRes.ok || !startData.success) throw new Error(startData.message || 'Failed to start passkey authentication');
    const credential = await startAuthentication(startData.options);

    const finishRes = await fetch(`${API_BASE_URL}/auth/mfa/passkey/auth/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, credential, rememberDevice })
    });
    const finishData = await finishRes.json();
    if (!finishRes.ok || !finishData.success) throw new Error(finishData.message || 'Failed to verify passkey');
    return finishData;
  },

  async mfaPasskeys(): Promise<Array<{ credentialId: string; createdAt?: string; lastUsedAt?: string; nickname?: string | null }>> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/passkeys`, { headers: authHeaders() });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load passkeys');
    return data.passkeys || [];
  },

  async mfaDeletePasskey(credentialId: string): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/passkeys/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || 'Failed to remove passkey');
  },

  async mfaResetPasskeys(currentPassword: string): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/passkeys/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ currentPassword })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || 'Failed to reset passkeys');
  },

  async mfaResetTotp(currentPassword: string): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/totp/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ currentPassword })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || 'Failed to reset TOTP');
  },

  async mfaRegenSelf(currentPassword: string): Promise<{ recoveryCodes: string[] }> {
    const res = await apiFetch(`${API_BASE_URL}/auth/mfa/recovery/regen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ currentPassword })
    })
    const data = await res.json()
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to regenerate codes')
    return data
  },

  async kioskDeviceExchange(
    deviceKey: string,
    opts?: { sessionCount?: number },
  ): Promise<{
    accessToken: string
    refreshToken: string
    refreshTokens: string[]
  }> {
    const sessionCount = Math.max(1, Math.min(8, Math.floor(Number(opts?.sessionCount) || 1)))
    const res = await fetch(`${API_BASE_URL}/auth/kiosk/device/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceKey, sessionCount }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.message || 'Device exchange failed')
    const refreshTokens = Array.isArray(data.refreshTokens)
      ? data.refreshTokens.map(String).filter(Boolean)
      : data.refreshToken
        ? [String(data.refreshToken)]
        : []
    if (!data.accessToken || refreshTokens.length === 0) {
      throw new Error(data.message || 'Device exchange failed')
    }
    return {
      accessToken: data.accessToken,
      refreshToken: refreshTokens[0],
      refreshTokens,
    }
  },

  async kioskDeviceRefresh(refreshToken: string): Promise<{
    accessToken: string
    refreshToken: string
  }> {
    const res = await fetch(`${API_BASE_URL}/auth/kiosk/device/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.message || 'Device refresh failed')
    return { accessToken: data.accessToken, refreshToken: data.refreshToken }
  },
}


